/*
 * Bringing in Country Select to accomidate new break points
 */
import React from 'react';

import { useIntl } from 'react-intl';

import countryList from '~/components/NewsletterSignup/countryList.json';
import * as screenSizes from '~/components/shared/viewPorts';
import { useAnalytics, Action } from '~/hooks/useAnalytics';
import { styled } from '~/styled';

const SelectWrapper = styled.div(({ theme }) => {
  return `
  position: relative;

  // Arrow for the select box
  &:after{
    position: absolute;
    content: '';
    right: ${theme.space.space16};
    bottom: ${theme.space.space24};
    width: 0;
    height: 0;
    border-radius: ${theme.radii.rounded};
    border-left: 6px solid transparent; // not in theme
    border-right: 6px solid transparent; // not in theme
    border-top: 5px solid ${theme.colors.n500}; // not in theme
    background: ${theme.colors.white};
    /* Want events to happen on select box */
    pointer-events: none;
  }

  ${screenSizes.mediaMaxWidthSmall} {
    max-width: 100%;
    width: 100%;
  }
`;
});

const Select = styled.select(({ theme }) => {
  return `
  flex-grow: 1;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.n90};
  border-radius: ${theme.radii.rounded};
  box-shadow: rgba(0, 0, 0, 0.075) 0px 1px 1px 0px inset;
  color: ${theme.colors.n500};
  font-size: ${theme.space.space16};
  font-weight: ${theme.fontWeights.regular};
  height: ${theme.space.space40};
  padding: ${theme.space.space4} ${theme.space.space32} ${theme.space.space4} ${theme.space.space12};
  margin: ${theme.space.space8} 0
    ${theme.space.space8} 0;
  box-shadow: none;
  line-height: ${theme.space.space24};
  max-width: 120px; // not in theme

  ${screenSizes.mediaMaxWidthSmall} {
    max-width: 100%;
    width: 100%;
  }

  &:invalid {
    color: ${theme.colors.n100};
  }
  .hasError &:invalid {
    border-color: ${theme.colors.red300};
    box-shadow: 0 0 ${theme.space.space4} ${theme.colors.red300};
  }
  &:valid {
    border-color: ${theme.colors.green300};
    box-shadow: 0 0 ${theme.space.space4} ${theme.colors.green300};
  }

  /* Do not want to see original drop down arrow */
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
`;
});

interface SelectProps {
  onChange: (e: any) => void;
  value: string;
}

const CountrySelect = ({ onChange, value }: SelectProps) => {
  const intl = useIntl();
  const { trackStandardizedAction } = useAnalytics();

  const countryPrimary = countryList.countries.find((country) => {
    return country.prioritize == intl.locale && country.prioritySort == 1;
  });
  const countrySecondary = countryList.countries.find((country) => {
    return country.prioritize == intl.locale && country.prioritySort == 2;
  });

  return (
    <SelectWrapper>
      <Select
        required
        name="country"
        onClick={() => {
          return trackStandardizedAction(
            'newsletter-country-select',
            Action.clicked,
          );
        }}
        onChange={(e) => {
          return onChange(e);
        }}
        value={value}
        data-testid="country-select"
      >
        <option disabled value="">
          {intl.formatMessage({ id: 'newsletterSignup.country' })}
        </option>
        {countryPrimary && (
          <React.Fragment>
            <option
              key={countryPrimary.value}
              value={countryPrimary.value}
              data-testid="option-primary"
            >
              {countryPrimary[intl.locale]}
            </option>
            {countrySecondary && (
              <option
                key={countrySecondary.value}
                value={countrySecondary.value}
                data-testid="option-secondary"
              >
                {countrySecondary[intl.locale]}
              </option>
            )}
          </React.Fragment>
        )}
        <option disabled>----</option>
        {countryList.countries.map((country) => {
          return (
            <option key={country.value} value={country.value}>
              {country[intl.locale]}
            </option>
          );
        })}
      </Select>
    </SelectWrapper>
  );
};

export default CountrySelect;
